import { apiFetch } from './http/client';
import type { DataResult } from './http/client';
import type { Team } from './team';

/** 1:1 port of `customer-apps/department/department.ts`. */
export interface DepartmentIdAndName {
    id: number
    name: string
    hierarchyName: string
}

export interface Department extends DepartmentIdAndName {
    isEditable: boolean
    hasImage: boolean
    image: string
    parentId?: string
    parentName?: string
    teams: Team[]
    childDepartments: Department[]
}

/** `GET /departments` — the club's department tree (nested, arbitrarily deep). */
export async function getDepartments(): Promise<Department[]> {
    const result = await apiFetch<DataResult<Department[]>>('/departments');
    return result.data ?? [];
}

/** `GET /departments/:id` — a single department with its children and teams. */
export async function getDepartment(id: number): Promise<Department> {
    const result = await apiFetch<DataResult<Department>>(`/departments/${id}`);
    return result.data;
}

/**
 * Angular `DepartmentService.getFlattenedDepartments()` — depth-first flattening of the tree.
 * Recursion is not optional: the test club's tree is three levels deep (Basketball → Junioren →
 * Damen), and a two-level walk silently loses more than half of the teams.
 */
export function flattenDepartments(departments: Department[]): Department[] {
    return departments.reduce<Department[]>((flattened, department) => {
        flattened.push(department);
        flattened.push(...flattenDepartments(department.childDepartments ?? []));
        return flattened;
    }, []);
}

export async function getFlattenedDepartments(): Promise<Department[]> {
    return flattenDepartments(await getDepartments());
}

/** Angular `DepartmentService.getTeamsForDepartments()` — every team below the given departments. */
export function getTeamsForDepartments(departments: Department[]): Team[] {
    return departments.reduce<Team[]>((teams, department) => {
        teams.push(...(department.teams ?? []));
        teams.push(...getTeamsForDepartments(department.childDepartments ?? []));
        return teams;
    }, []);
}
